/* eslint-disable eqeqeq */
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import {
    Grid, Typography, Button, Divider, CircularProgress,
    Paper, IconButton, MenuItem, Select, InputLabel,
    FormControl, FormHelperText, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Chip,
    Checkbox, FormControlLabel,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { HttpRequest } from "../../../utils/httpRequests";
import NavBar from "../../../components/NavBar/NavBar";
import CustomizedSnackbar from "../../../components/CustomizedSnackbar/CustomizedSnackbar";
import LoginMenu from "../../../components/LoginMenu/LoginMenu";

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

// ── Image URL builder ─────────────────────────────────────────────────────────
const getImageUrl = (fileUrl) => {
    if (!fileUrl) return "";
    // If it's already an absolute URL (blob: or http), return as-is
    if (fileUrl.startsWith("blob:") || fileUrl.startsWith("http")) return fileUrl;
    return `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/albums/C01ImageView?fileName=${fileUrl}`;
};

const useStyles = () => ({
    root: { padding: 10, margin: "70px 20px" },

    sectionCard: {
        border: "1px solid #e0e0e0",
        borderRadius: 10,
        padding: "20px 24px",
        marginTop: 24,
        backgroundColor: "#fafafa",
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: 16,
        color: "#1565c0",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    subSectionHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 20,
        marginBottom: 8,
    },
    subSectionLabel: {
        fontWeight: 600,
        fontSize: 13,
        color: "#333",
    },

    // ── Horizontal scroll strip ───────────────────────────────────────────────
    hScroll: {
        display: "flex",
        flexDirection: "row",
        gap: 10,
        overflowX: "auto",
        paddingBottom: 8,
        paddingTop: 4,
        minHeight: 80,
        alignItems: "center",
        "&::-webkit-scrollbar": { height: 5 },
        "&::-webkit-scrollbar-thumb": { background: "#ccc", borderRadius: 4 },
    },
    emptyStrip: {
        color: "#bbb",
        fontSize: 12,
        fontStyle: "italic",
        padding: "8px 4px",
    },

    // ── Item card ─────────────────────────────────────────────────────────────
    itemCard: {
        minWidth: 82,
        maxWidth: 100,
        backgroundColor: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: "8px 8px 4px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        flex: "0 0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    },
    itemCardWide: {
        minWidth: 130,
        maxWidth: 150,
    },
    itemDeleteBtn: {
        position: "absolute",
        top: 2,
        right: 2,
        padding: 2,
    },
    itemLabel: {
        fontSize: 11,
        color: "#555",
        textAlign: "center",
        marginTop: 4,
        maxWidth: 120,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    colorSwatch: {
        width: 46,
        height: 46,
        borderRadius: 8,
        border: "1px solid #ddd",
    },
    gradientBar: {
        width: 90,
        height: 38,
        borderRadius: 8,
        border: "1px solid #ddd",
    },
    thumb: {
        width: 60,
        height: 60,
        objectFit: "cover",
        borderRadius: 6,
        border: "1px solid #ddd",
    },

    // ── + add card in the strip ───────────────────────────────────────────────
    addCard: {
        minWidth: 58,
        height: 58,
        backgroundColor: "#f0f4ff",
        border: "2px dashed #90caf9",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flex: "0 0 auto",
        transition: "background 0.15s",
    },

    // ── Dialog ────────────────────────────────────────────────────────────────
    dialogTitleRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },

    // ── File uploader ─────────────────────────────────────────────────────────
    fileUploadBox: {
        border: "2px dashed #90caf9",
        borderRadius: 8,
        backgroundColor: "#f0f4ff",
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        transition: "background 0.15s",
    },
    fileUploadBoxDone: {
        border: "2px solid #66bb6a",
        backgroundColor: "#f1f8f1",
    },
    filePreviewImg: {
        maxHeight: 80,
        maxWidth: "100%",
        borderRadius: 6,
        border: "1px solid #ddd",
        objectFit: "cover",
        marginTop: 6,
    },
});

// ── Small helper: empty state text ────────────────────────────────────────────
const EmptyStrip = ({ classes, label }) => (
    <span className={classes.emptyStrip}>{label || "Nothing added yet — click + to add."}</span>
);

class F21ProductEditorConfigForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            // ── Category ──────────────────────────────────────────────────────
            categoriesData: [],
            selectedCategoryId: "",
            selectedCategoryIdError: "",
            isCategoriesLoading: false,

            // ── Loaded config ─────────────────────────────────────────────────
            existingConfig: null,
            isConfigLoading: false,

            // ── Sticker category filter ───────────────────────────────────────
            selectedStickerCategoryId: "",

            // ── Which popup is open ───────────────────────────────────────────
            // null | 'textColor' | 'font' | 'solidColor' | 'gradient'
            //      | 'textureCategory' | 'textureItem'
            //      | 'stickerCategory' | 'stickerItem'
            popup: null,

            // ── popup field values ────────────────────────────────────────────
            popTextColor: "", popTextColorError: "", popTextColorIsForCover: false,
            popFontLabel: "", popFontLabelError: "", popFontIsForCover: false,
            popFontFileName: "", popFontFileNameError: "",
            popFontFile: null, popFontFilePreview: "",   // ← file upload
            popFontBaseSizeUnits: "",
            popSolidColor: "", popSolidColorError: "",
            popGradName: "", popGradNameError: "",
            popGradAngle: "", popGradAngleError: "",
            popGradColors: [],
            popGradColorInput: "",
            popTexCatLabel: "", popTexCatLabelError: "",
            popTexItemCatId: "", popTexItemCatIdError: "",
            popTexItemLabel: "",
            popTexItemFileName: "", popTexItemFileNameError: "",
            popTexItemFile: null, popTexItemFilePreview: "", // ← file upload
            popSticCatLabel: "", popSticCatLabelError: "",
            popSticItemCatId: "", popSticItemCatIdError: "",
            popSticItemFileName: "", popSticItemFileNameError: "",
            popSticItemFile: null, popSticItemFilePreview: "", // ← file upload

            // ── Cover Design — Solid Color ────────────────────────────────────
            popCoverSolidColor: "", popCoverSolidColorError: "",

            // ── Cover Design — Pattern ────────────────────────────────────────
            popPatternSizeId: "", popPatternSizeIdError: "",
            popPatternCoverTypeId: "", popPatternCoverTypeIdError: "",
            popPatternPageNumbers: [], popPatternPageNumbersError: "",
            popPatternPageTypeId: "", popPatternPageTypeIdError: "",
            popPatternIcFile: null, popPatternIcFilePreview: "", popPatternIcFileError: "",
            popPatternFlFile: null, popPatternFlFilePreview: "", popPatternFlFileError: "",
            popPatternIsBackCoverSame: true,
            popPatternBackCoverColor: "", popPatternBackCoverColorError: "",
            patternSizes: [], patternPageNumbers: [], patternCoverTypes: [], patternPageTypes: [],
            isPatternDataLoading: false,

            // ── Cover Design — TwoTone Color ──────────────────────────────────
            popTwoToneName: "", popTwoToneNameError: "",
            popTwoToneMasterColor: "", popTwoToneMasterColorError: "",
            popTwoToneMasterPct: "", popTwoToneMasterPctError: "",
            popTwoToneSeparatorColor: "", popTwoToneSeparatorColorError: "",
            popTwoToneSeparatorPct: "", popTwoToneSeparatorPctError: "",
            popTwoToneChildColor: "", popTwoToneChildColorError: "",
            popTwoToneChildPct: "", popTwoToneChildPctError: "",

            // ── Loading ───────────────────────────────────────────────────────
            isSaving: false,

            // ── Snackbar / login ──────────────────────────────────────────────
            isOpenSnackbar: false, snackbarMessage: "", snackbarSeverity: "",
            isLoginMenu: false, isReload: true,
        };
    }

    componentDidMount() { this.loadCategories(); }

    snack = (open, msg, sev) => this.setState({ isOpenSnackbar: open, snackbarMessage: msg, snackbarSeverity: sev });

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value || "" });
    };

    // ── File picker handler — used for font, texture item, sticker item ───────
    handleFileChange = (fileStateKey, previewKey) => (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const preview = URL.createObjectURL(file);
        this.setState({ [fileStateKey]: file, [previewKey]: preview });
    };

    // ── Reusable file upload UI block ─────────────────────────────────────────
    renderFileUploader = (classes, fileStateKey, previewKey, accept, label) => {
        const file = this.state[fileStateKey];
        const preview = this.state[previewKey];
        const inputId = `fileInput_${fileStateKey}`;
        const isImage = accept && accept.includes("image");
        return (
            <div>
                <input
                    id={inputId}
                    type="file"
                    accept={accept || "*"}
                    style={{ display: "none" }}
                    onChange={this.handleFileChange(fileStateKey, previewKey)}
                />
                <label htmlFor={inputId}>
                    <div className={`${classes.fileUploadBox} ${file ? classes.fileUploadBoxDone : ""}`}>
                        {file ? (
                            <>
                                <span style={{ fontSize: 20 }}>✅</span>
                                <Typography variant="caption" style={{ color: "#388e3c", fontWeight: 600, textAlign: "center", wordBreak: "break-all" }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="caption" style={{ color: "#888" }}>
                                    Click to change
                                </Typography>
                            </>
                        ) : (
                            <>
                                <span style={{ fontSize: 24 }}>📁</span>
                                <Typography variant="caption" style={{ color: "#1976d2", fontWeight: 600 }}>
                                    {label || "Click to upload file"}
                                </Typography>
                                <Typography variant="caption" style={{ color: "#999" }}>
                                    {accept || "Any file"}
                                </Typography>
                            </>
                        )}
                    </div>
                </label>
                {/* Preview — shown for image uploads */}
                {isImage && preview && (
                    <img src={preview} alt="preview" className={classes.filePreviewImg} />
                )}
            </div>
        );
    };

    openPopup = (name) => this.setState({ popup: name });
    closePopup = () => this.setState({ popup: null });

    BASE = () => `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common`;

    // ── Load categories ───────────────────────────────────────────────────────
    loadCategories = async () => {
        this.setState({ isCategoriesLoading: true });
        const res = await HttpRequest("GET", `${this.BASE()}/C21CommonProductsCategoriesView`, null, "authorizeJson");
        if (res.isSuccess) this.setState({ categoriesData: res.data || [] });
        else if (res.statusCode === 401) this.setState({ isLoginMenu: true });
        else this.snack(true, res.message || "Failed to load categories.", "error");
        this.setState({ isCategoriesLoading: false });
    };

    // ── Load config ───────────────────────────────────────────────────────────
    loadConfig = async () => {
        const { selectedCategoryId } = this.state;
        if (!selectedCategoryId) { this.setState({ selectedCategoryIdError: "Please select a category." }); return; }
        this.setState({ isConfigLoading: true, existingConfig: null, selectedCategoryIdError: "" });
        const res = await HttpRequest("GET",
            `${this.BASE()}/C21ProductEditorConfigView?categoryId=${selectedCategoryId}`, null, "authorizeJson");
        if (res.isSuccess) {
            const d = res.data[0].config || {};
            const config = {
                text: { colors: d.text?.colors || [], fonts: d.text?.fonts || [] },
                borders: {
                    solidColors: d.borders?.solidColors || [],
                    gradients: d.borders?.gradients || [],
                    textures: d.borders?.textures || [],
                },
                stickers: d.stickers || [],
                coverDesigns: {
                    solidColors: d.coverDesigns?.solidColors || [],
                    groupedPatterns: d.coverDesigns?.groupedPatterns || [],
                    twoTuneColors: d.coverDesigns?.twoTuneColors || [],
                },
            };
            const firstCat = config.stickers.length > 0 ? String(config.stickers[0].id) : "";
            this.setState({ existingConfig: config, selectedStickerCategoryId: firstCat });
            if (String(selectedCategoryId) === "1") { this.loadPatternDropdowns(); }
            if (String(selectedCategoryId) === "1") this.loadPatternDropdowns();
            if (!res.data) this.snack(true, "No config found — start adding entries below.", "info");
        } else {
            if (res.statusCode === 401) this.setState({ isLoginMenu: true });
            else this.snack(true, res.message || "Failed to load config.", "error");
        }
        this.setState({ isConfigLoading: false });
    };

    // ── POST then reload ──────────────────────────────────────────────────────
    post = async (url, data, resetFn) => {
        this.setState({ isSaving: true });
        const res = await HttpRequest("POST", url, data, "authorize");
        if (res.isSuccess) {
            this.snack(true, res.data || "Saved successfully.", "success");
            this.closePopup();
            if (resetFn) resetFn();
            this.loadConfig();
        } else {
            if (res.statusCode === 401) this.setState({ isLoginMenu: true });
            else this.snack(true, res.message || "Save failed.", "error");
        }
        this.setState({ isSaving: false });
    };

    del = async (url, id) => {
        const d = new FormData(); d.append("id", id);
        const res = await HttpRequest("POST", url, d, "authorize");
        if (res.isSuccess) { this.snack(true, "Removed.", "success"); this.loadConfig(); }
        else if (res.statusCode === 401) this.setState({ isLoginMenu: true });
        else this.snack(true, res.message || "Delete failed.", "error");
    };

    // ── Saves ─────────────────────────────────────────────────────────────────
    saveTextColor = async () => {
        const { popTextColor, popTextColorIsForCover, selectedCategoryId } = this.state;
        if (!HEX_REGEX.test(popTextColor)) { this.setState({ popTextColorError: "Enter valid hex e.g. #FF5733" }); return; }
        this.setState({ popTextColorError: "" });
        const d = new FormData();
        d.append("categoryId", selectedCategoryId);
        d.append("hexColor", popTextColor);
        d.append("isForCover", popTextColorIsForCover ? "1" : "0");
        await this.post(`${this.BASE()}/C21ProductEditorTextColorSave`, d, () => this.setState({ popTextColor: "", popTextColorIsForCover: false }));
    };

    saveFont = async () => {
        const { popFontLabel, popFontFileName, popFontFile, popFontBaseSizeUnits, popFontIsForCover, selectedCategoryId } = this.state;
        let err = false;
        if (!popFontLabel.trim()) { this.setState({ popFontLabelError: "Required" }); err = true; } else this.setState({ popFontLabelError: "" });
        const resolvedFileName = popFontFile ? popFontFile.name : popFontFileName.trim();
        if (!resolvedFileName) { this.setState({ popFontFileNameError: "Upload a font file or enter a file path" }); err = true; } else this.setState({ popFontFileNameError: "" });
        if (err) return;
        const d = new FormData();
        d.append("categoryId", selectedCategoryId);
        d.append("label", popFontLabel.trim());
        d.append("fileName", resolvedFileName);
        d.append("baseSizeUnits", popFontBaseSizeUnits || "0");
        d.append("isForCover", popFontIsForCover ? "1" : "0");
        if (popFontFile) d.append("file", popFontFile);
        await this.post(`${this.BASE()}/C21ProductEditorFontSave`, d,
            () => this.setState({ popFontLabel: "", popFontFileName: "", popFontBaseSizeUnits: "", popFontFile: null, popFontFilePreview: "", popFontIsForCover: false }));
    };

    saveSolidColor = async () => {
        const { popSolidColor, selectedCategoryId } = this.state;
        if (!HEX_REGEX.test(popSolidColor)) { this.setState({ popSolidColorError: "Enter valid hex e.g. #FF0000" }); return; }
        this.setState({ popSolidColorError: "" });
        const d = new FormData();
        d.append("categoryId", selectedCategoryId); d.append("hexColor", popSolidColor);
        await this.post(`${this.BASE()}/C21ProductEditorBorderSolidColorSave`, d, () => this.setState({ popSolidColor: "" }));
    };

    addGradChip = () => {
        const { popGradColorInput, popGradColors } = this.state;
        if (!HEX_REGEX.test(popGradColorInput)) { this.snack(true, "Enter a valid hex color", "warning"); return; }
        this.setState({ popGradColors: [...popGradColors, popGradColorInput], popGradColorInput: "" });
    };
    removeGradChip = (i) => this.setState({ popGradColors: this.state.popGradColors.filter((_, idx) => idx !== i) });

    saveGradient = async () => {
        const { popGradName, popGradAngle, popGradColors, selectedCategoryId } = this.state;
        let err = false;
        if (!popGradName.trim()) { this.setState({ popGradNameError: "Required" }); err = true; } else this.setState({ popGradNameError: "" });
        if (popGradAngle === "" || isNaN(Number(popGradAngle))) { this.setState({ popGradAngleError: "e.g. 135" }); err = true; } else this.setState({ popGradAngleError: "" });
        if (popGradColors.length < 2) { this.snack(true, "Add at least 2 colors", "warning"); err = true; }
        if (err) return;
        const d = new FormData();
        d.append("categoryId", selectedCategoryId); d.append("name", popGradName.trim());
        d.append("angleInDegrees", popGradAngle); d.append("colors", JSON.stringify(popGradColors));
        await this.post(`${this.BASE()}/C21ProductEditorGradientSave`, d,
            () => this.setState({ popGradName: "", popGradAngle: "", popGradColors: [], popGradColorInput: "" }));
    };

    saveTextureCategory = async () => {
        const { popTexCatLabel, selectedCategoryId } = this.state;
        if (!popTexCatLabel.trim()) { this.setState({ popTexCatLabelError: "Required" }); return; }
        this.setState({ popTexCatLabelError: "" });
        const d = new FormData();
        d.append("categoryId", selectedCategoryId); d.append("categoryLabel", popTexCatLabel.trim());
        await this.post(`${this.BASE()}/C21ProductEditorTextureCategorySave`, d, () => this.setState({ popTexCatLabel: "" }));
    };

    saveTextureItem = async () => {
        const { popTexItemCatId, popTexItemLabel, popTexItemFileName, popTexItemFile } = this.state;
        let err = false;
        if (!popTexItemCatId) { this.setState({ popTexItemCatIdError: "Select category" }); err = true; } else this.setState({ popTexItemCatIdError: "" });
        const resolvedFileName = popTexItemFile ? popTexItemFile.name : popTexItemFileName.trim();
        if (!resolvedFileName) { this.setState({ popTexItemFileNameError: "Upload an image or enter a file path" }); err = true; } else this.setState({ popTexItemFileNameError: "" });
        if (err) return;
        const d = new FormData();
        d.append("textureCategoryId", popTexItemCatId);
        d.append("label", popTexItemLabel.trim());
        d.append("fileName", resolvedFileName);
        if (popTexItemFile) d.append("file", popTexItemFile);   // ← actual file bytes
        await this.post(`${this.BASE()}/C21ProductEditorTextureItemSave`, d,
            () => this.setState({ popTexItemCatId: "", popTexItemLabel: "", popTexItemFileName: "", popTexItemFile: null, popTexItemFilePreview: "" }));
    };

    saveStickerCategory = async () => {
        const { popSticCatLabel, selectedCategoryId } = this.state;
        if (!popSticCatLabel.trim()) { this.setState({ popSticCatLabelError: "Required" }); return; }
        this.setState({ popSticCatLabelError: "" });
        const d = new FormData();
        d.append("categoryId", selectedCategoryId); d.append("categoryLabel", popSticCatLabel.trim());
        await this.post(`${this.BASE()}/C21ProductEditorStickerCategorySave`, d, () => this.setState({ popSticCatLabel: "" }));
    };

    saveStickerItem = async () => {
        const { popSticItemCatId, popSticItemFileName, popSticItemFile } = this.state;
        let err = false;
        if (!popSticItemCatId) { this.setState({ popSticItemCatIdError: "Select category" }); err = true; } else this.setState({ popSticItemCatIdError: "" });
        const resolvedFileName = popSticItemFile ? popSticItemFile.name : popSticItemFileName.trim();
        if (!resolvedFileName) { this.setState({ popSticItemFileNameError: "Upload an image or enter a file path" }); err = true; } else this.setState({ popSticItemFileNameError: "" });
        if (err) return;
        const d = new FormData();
        d.append("stickerCategoryId", popSticItemCatId);
        d.append("fileName", resolvedFileName);
        if (popSticItemFile) d.append("file", popSticItemFile);  // ← actual file bytes
        await this.post(`${this.BASE()}/C21ProductEditorStickerItemSave`, d,
            () => this.setState({ popSticItemCatId: "", popSticItemFileName: "", popSticItemFile: null, popSticItemFilePreview: "" }));
    };

    // ── Load pattern dropdown data (sizes, page numbers, cover types) ─────────
    loadPatternDropdowns = async () => {
        this.setState({ isPatternDataLoading: true });
        const { selectedCategoryId } = this.state;
        const [sizesRes, pageNumRes, coverTypeRes, pageTypeRes] = await Promise.all([
            HttpRequest("GET", `${this.BASE()}/C21ProductEditorPatternSizesView?categoryId=${selectedCategoryId}`, null, "authorizeJson"),
            HttpRequest("GET", `${this.BASE()}/C21ProductEditorPatternPageNumbersView?categoryId=${selectedCategoryId}`, null, "authorizeJson"),
            HttpRequest("GET", `${this.BASE()}/C21ProductEditorPatternCoverTypesView?categoryId=${selectedCategoryId}`, null, "authorizeJson"),
            HttpRequest("GET", `${this.BASE()}/C21CommonProductsPagesTypesView?categoryId=${selectedCategoryId}`, null, "authorizeJson"),
        ]);
        this.setState({
            patternSizes: sizesRes.isSuccess ? (sizesRes.data || []) : [],
            patternPageNumbers: pageNumRes.isSuccess ? (pageNumRes.data || []) : [],
            patternCoverTypes: coverTypeRes.isSuccess ? (coverTypeRes.data || []) : [],
            patternPageTypes: pageTypeRes.isSuccess ? (pageTypeRes.data || []) : [],
            isPatternDataLoading: false,
        });
    };

    saveCoverSolidColor = async () => {
        const { popCoverSolidColor, selectedCategoryId } = this.state;
        if (!HEX_REGEX.test(popCoverSolidColor)) { this.setState({ popCoverSolidColorError: "Enter valid hex e.g. #FF0000" }); return; }
        this.setState({ popCoverSolidColorError: "" });
        const d = new FormData();
        d.append("categoryId", selectedCategoryId); d.append("hexColor", popCoverSolidColor);
        await this.post(`${this.BASE()}/C21ProductEditorCoverDesignSolidColorSave`, d, () => this.setState({ popCoverSolidColor: "" }));
    };

    savePattern = async () => {
        const { popPatternSizeId, popPatternCoverTypeId, popPatternPageNumbers, popPatternPageTypeId,
            popPatternIcFile, popPatternFlFile, popPatternIsBackCoverSame, popPatternBackCoverColor,
            selectedCategoryId, patternSizes, patternPageNumbers, patternCoverTypes, patternPageTypes } = this.state;
        let err = false;
        if (!popPatternSizeId) { this.setState({ popPatternSizeIdError: "Select a size" }); err = true; } else this.setState({ popPatternSizeIdError: "" });
        if (!popPatternCoverTypeId) { this.setState({ popPatternCoverTypeIdError: "Select cover type" }); err = true; } else this.setState({ popPatternCoverTypeIdError: "" });
        if (!popPatternPageNumbers.length) { this.setState({ popPatternPageNumbersError: "Select at least one page number" }); err = true; } else this.setState({ popPatternPageNumbersError: "" });
        if (!popPatternPageTypeId) { this.setState({ popPatternPageTypeIdError: "Select page type" }); err = true; } else this.setState({ popPatternPageTypeIdError: "" });
        if (!popPatternIcFile) { this.setState({ popPatternIcFileError: "Upload IC Asset" }); err = true; } else this.setState({ popPatternIcFileError: "" });
        if (!popPatternFlFile) { this.setState({ popPatternFlFileError: "Upload FL Asset" }); err = true; } else this.setState({ popPatternFlFileError: "" });
        if (!popPatternIsBackCoverSame && !HEX_REGEX.test(popPatternBackCoverColor)) { this.setState({ popPatternBackCoverColorError: "Enter valid hex e.g. #FF0000" }); err = true; } else this.setState({ popPatternBackCoverColorError: "" });
        if (err) return;
        const sizeLabel = patternSizes.find(s => String(s.id) === String(popPatternSizeId))?.label || popPatternSizeId;
        const coverLabel = patternCoverTypes.find(c => String(c.id) === String(popPatternCoverTypeId))?.label || popPatternCoverTypeId;
        const pageTypeLabel = patternPageTypes.find(pt => String(pt.id) === String(popPatternPageTypeId))?.label || popPatternPageTypeId;
        const label = `${sizeLabel}_${coverLabel}_${pageTypeLabel}_${popPatternPageNumbers.join("_")}`;
        const d = new FormData();
        d.append("categoryId", selectedCategoryId);
        d.append("sizeId", popPatternSizeId);
        d.append("coverTypeId", popPatternCoverTypeId);
        popPatternPageNumbers.forEach(id => d.append("pageNumbers", id));
        d.append("pageTypeId", popPatternPageTypeId);
        d.append("label", label);
        d.append("icFile", popPatternIcFile);
        d.append("flFile", popPatternFlFile);
        d.append("isBackCoverSame", popPatternIsBackCoverSame ? "true" : "false");
        if (!popPatternIsBackCoverSame) d.append("backCoverColor", popPatternBackCoverColor);
        await this.post(`${this.BASE()}/C21ProductEditorPatternSave`, d,
            () => this.setState({ popPatternSizeId: "", popPatternCoverTypeId: "", popPatternPageNumbers: [], popPatternPageTypeId: "", popPatternIcFile: null, popPatternIcFilePreview: "", popPatternIcFileError: "", popPatternFlFile: null, popPatternFlFilePreview: "", popPatternFlFileError: "", popPatternIsBackCoverSame: true, popPatternBackCoverColor: "", popPatternBackCoverColorError: "" }));
    };

    saveTwoToneColor = async () => {
        const { popTwoToneName, popTwoToneMasterColor, popTwoToneSeparatorColor, popTwoToneChildColor, selectedCategoryId } = this.state;
        let err = false;
        if (!popTwoToneName.trim()) { this.setState({ popTwoToneNameError: "Required" }); err = true; } else this.setState({ popTwoToneNameError: "" });
        if (!HEX_REGEX.test(popTwoToneMasterColor)) { this.setState({ popTwoToneMasterColorError: "Valid hex required" }); err = true; } else this.setState({ popTwoToneMasterColorError: "" });
        if (!HEX_REGEX.test(popTwoToneSeparatorColor)) { this.setState({ popTwoToneSeparatorColorError: "Valid hex required" }); err = true; } else this.setState({ popTwoToneSeparatorColorError: "" });
        if (!HEX_REGEX.test(popTwoToneChildColor)) { this.setState({ popTwoToneChildColorError: "Valid hex required" }); err = true; } else this.setState({ popTwoToneChildColorError: "" });
        if (err) return;
        const d = new FormData();
        d.append("categoryId", selectedCategoryId);
        d.append("name", popTwoToneName.trim());
        d.append("masterColor", popTwoToneMasterColor);
        d.append("masterColorPercentage", 79);
        d.append("separatorColor", popTwoToneSeparatorColor);
        d.append("separatorColorPercentage", 2);
        d.append("childColor", popTwoToneChildColor);
        d.append("childColorPercentage", 19);
        await this.post(`${this.BASE()}/C21ProductEditorTwoToneColorSave`, d,
            () => this.setState({ popTwoToneName: "", popTwoToneMasterColor: "", popTwoToneSeparatorColor: "", popTwoToneChildColor: "" }));
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render helper: sub-section header with inline + icon button
    // ─────────────────────────────────────────────────────────────────────────
    subHeader = (classes, label, popupKey) => (
        <div className={classes.subSectionHeader}>
            <span className={classes.subSectionLabel}>{label}</span>
            <IconButton size="small" color="primary" onClick={() => this.openPopup(popupKey)}
                style={{ backgroundColor: "#e8f0fe", borderRadius: 6, padding: 4 }} title={`Add ${label}`}>
                <AddIcon fontSize="small" />
            </IconButton>
        </div>
    );

    // Render the + dashed add card inside a strip
    addCard = (classes, popupKey, overrideFn) => (
        <div className={classes.addCard}
            onClick={overrideFn || (() => this.openPopup(popupKey))}
            title="Add new">
            <AddIcon style={{ color: "#1976d2", fontSize: 22 }} />
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    render() {
        const { classes } = this.props;
        const {
            categoriesData, selectedCategoryId, selectedCategoryIdError, isCategoriesLoading,
            existingConfig, isConfigLoading, selectedStickerCategoryId, popup, isSaving,
            popTextColor, popTextColorError, popTextColorIsForCover,
            popFontLabel, popFontFileName, popFontFile, popFontFilePreview, popFontBaseSizeUnits, popFontLabelError, popFontFileNameError, popFontIsForCover,
            popSolidColor, popSolidColorError,
            popGradName, popGradAngle, popGradColors, popGradColorInput, popGradNameError, popGradAngleError,
            popTexCatLabel, popTexCatLabelError,
            popTexItemCatId, popTexItemLabel, popTexItemFileName, popTexItemFile, popTexItemFilePreview, popTexItemCatIdError, popTexItemFileNameError,
            popSticCatLabel, popSticCatLabelError,
            popSticItemCatId, popSticItemFileName, popSticItemFile, popSticItemFilePreview, popSticItemCatIdError, popSticItemFileNameError,
            popCoverSolidColor, popCoverSolidColorError,
            popPatternSizeId, popPatternSizeIdError,
            popPatternCoverTypeId, popPatternCoverTypeIdError,
            popPatternPageNumbers, popPatternPageNumbersError,
            popPatternPageTypeId, popPatternPageTypeIdError,
            popPatternIcFile, popPatternIcFilePreview, popPatternIcFileError,
            popPatternFlFile, popPatternFlFilePreview, popPatternFlFileError,
            popPatternIsBackCoverSame, popPatternBackCoverColor, popPatternBackCoverColorError,
            patternSizes, patternPageNumbers, patternCoverTypes, patternPageTypes, isPatternDataLoading,
            popTwoToneName, popTwoToneNameError, popTwoToneMasterColor, popTwoToneMasterColorError,
            popTwoToneMasterPct, popTwoToneMasterPctError, popTwoToneSeparatorColor, popTwoToneSeparatorColorError,
            popTwoToneSeparatorPct, popTwoToneSeparatorPctError, popTwoToneChildColor, popTwoToneChildColorError,
            popTwoToneChildPct, popTwoToneChildPctError,
        } = this.state;

        const cfg = existingConfig;
        const textColors = cfg?.text?.colors || [];
        const fonts = cfg?.text?.fonts || [];
        const solidColors = cfg?.borders?.solidColors || [];
        const gradients = cfg?.borders?.gradients || [];
        const texCats = cfg?.borders?.textures || [];
        const stickerCats = cfg?.stickers || [];
        const activeStickerCat = stickerCats.find(sc => String(sc.id) === String(selectedStickerCategoryId));
        const stickerItems = activeStickerCat?.items || [];
        const coverSolidColors = cfg?.coverDesigns?.solidColors || [];
        const groupedPatterns = cfg?.coverDesigns?.groupedPatterns || [];
        const twoTuneColors = cfg?.coverDesigns?.twoTuneColors || [];
        const isCat1 = String(selectedCategoryId) === "1";

        return (
            <Fragment>
                <LoginMenu reload={this.state.isReload} open={this.state.isLoginMenu}
                    handleClose={() => this.setState({ isLoginMenu: false })} />
                <CustomizedSnackbar isOpen={this.state.isOpenSnackbar} message={this.state.snackbarMessage}
                    severity={this.state.snackbarSeverity}
                    handleCloseSnackbar={() => this.snack(false, "", "")} autoHideDuration={3000} />
                <NavBar isAuthorize />

                <div className={classes.root}>

                    {/* ── Page title ──────────────────────────────────────── */}
                    <Typography variant="h5" color="primary" style={{ fontWeight: 700 }}>
                        Product Editor Config
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                        Configure text, borders and sticker assets per product category.
                    </Typography>
                    <Divider style={{ marginTop: 12, marginBottom: 20 }} />

                    {/* ── Category selector ───────────────────────────────── */}
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth error={!!selectedCategoryIdError}>
                                <InputLabel>Product Category</InputLabel>
                                <Select name="selectedCategoryId" value={selectedCategoryId}
                                    onChange={this.handleChange} label="Product Category"
                                    disabled={isCategoriesLoading}>
                                    {isCategoriesLoading
                                        ? <MenuItem value=""><em>Loading…</em></MenuItem>
                                        : categoriesData.length === 0
                                            ? <MenuItem value=""><em>No categories</em></MenuItem>
                                            : categoriesData.map(cat => (
                                                <MenuItem key={cat.id} value={cat.id}>
                                                    {cat.id} — {cat.label}
                                                </MenuItem>
                                            ))}
                                </Select>
                                {selectedCategoryIdError && <FormHelperText>{selectedCategoryIdError}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button variant="contained" color="primary" onClick={this.loadConfig}
                                disabled={isConfigLoading || !selectedCategoryId}
                                style={{ height: 56, textTransform: "capitalize", minWidth: 130 }}>
                                {isConfigLoading
                                    ? <CircularProgress size={22} style={{ color: "#fff" }} />
                                    : "Load Config"}
                            </Button>
                        </Grid>
                    </Grid>

                    {/* ══════════════════════════════════════════════════════ */}
                    {/* Sections — only visible after config loaded           */}
                    {/* ══════════════════════════════════════════════════════ */}
                    {cfg && (
                        <Fragment>

                            {/* ════════════════════════════════════════════ */}
                            {/* SECTION 1 — TEXT                            */}
                            {/* ════════════════════════════════════════════ */}
                            <Paper className={classes.sectionCard} elevation={0}>
                                <div className={classes.sectionTitle}>
                                    <span>🔤</span> Text
                                </div>

                                {/* 1.1 Colors */}
                                {this.subHeader(classes, "Colors", "textColor")}
                                <div className={classes.hScroll}>
                                    {textColors.length === 0 && <EmptyStrip classes={classes} />}
                                    {textColors.map((c, i) => (
                                        <div key={i} className={classes.itemCard}>
                                            <div className={classes.colorSwatch} style={{ backgroundColor: c.colorCode }} />
                                            <span className={classes.itemLabel}>{c.colorCode}</span>
                                            {c.isForCover == 1 && (
                                                <span style={{ fontSize: 9, background: "#1565c0", color: "#fff", borderRadius: 3, padding: "1px 4px", marginTop: 2 }}>Cover</span>
                                            )}
                                            <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorTextColorDelete`, i)}>
                                                <DeleteIcon style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {this.addCard(classes, "textColor")}
                                </div>

                                <Divider style={{ marginTop: 16 }} />

                                {/* 1.2 Fonts */}
                                {this.subHeader(classes, "Fonts", "font")}
                                {/* inject @font-face for each loaded font */}
                                {fonts.length > 0 && (
                                    <style>{fonts.map(f => `
                                        @font-face {
                                            font-family: "ck_font_${f.id}";
                                            src: url("${getImageUrl(f.fileName)}") format("truetype");
                                        }
                                    `).join("")}</style>
                                )}
                                <div className={classes.hScroll}>
                                    {fonts.length === 0 && <EmptyStrip classes={classes} />}
                                    {fonts.map(f => (
                                        <div key={f.id} className={`${classes.itemCard} ${classes.itemCardWide}`}>
                                            <span style={{ fontSize: 28, fontFamily: `"ck_font_${f.id}", serif`, lineHeight: 1.2 }}>Aa</span>
                                            <span className={classes.itemLabel}>{f.label}</span>
                                            <span style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
                                                size: {f.baseSizeUnits || "—"}
                                            </span>
                                            {f.isForCover == 1 && (
                                                <span style={{ fontSize: 9, background: "#1565c0", color: "#fff", borderRadius: 3, padding: "1px 4px", marginTop: 2 }}>Cover</span>
                                            )}
                                            <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorFontDelete`, f.id)}>
                                                <DeleteIcon style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {this.addCard(classes, "font")}
                                </div>
                            </Paper>

                            {/* ════════════════════════════════════════════ */}
                            {/* SECTION 2 — BORDERS                         */}
                            {/* ════════════════════════════════════════════ */}
                            {!isCat1 && <Paper className={classes.sectionCard} elevation={0}>
                                <div className={classes.sectionTitle}>
                                    <span>🖼</span> Borders
                                </div>

                                {/* 2.1 Solid Colors */}
                                {this.subHeader(classes, "Solid Colors", "solidColor")}
                                <div className={classes.hScroll}>
                                    {solidColors.length === 0 && <EmptyStrip classes={classes} />}
                                    {solidColors.map((hex, i) => (
                                        <div key={i} className={classes.itemCard}>
                                            <div className={classes.colorSwatch} style={{ backgroundColor: hex }} />
                                            <span className={classes.itemLabel}>{hex}</span>
                                            <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorBorderSolidColorDelete`, i)}>
                                                <DeleteIcon style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {this.addCard(classes, "solidColor")}
                                </div>

                                <Divider style={{ marginTop: 16 }} />

                                {/* 2.2 Gradients */}
                                {this.subHeader(classes, "Gradients", "gradient")}
                                <div className={classes.hScroll}>
                                    {gradients.length === 0 && <EmptyStrip classes={classes} />}
                                    {gradients.map(g => (
                                        <div key={g.id} className={`${classes.itemCard} ${classes.itemCardWide}`}>
                                            <div className={classes.gradientBar} style={{
                                                background: `linear-gradient(${g.angleInDegrees}deg, ${(g.colors || []).join(", ")})`
                                            }} />
                                            <span className={classes.itemLabel}>{g.name}</span>
                                            <span style={{ fontSize: 10, color: "#aaa" }}>{g.angleInDegrees}°</span>
                                            <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorGradientDelete`, g.id)}>
                                                <DeleteIcon style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {this.addCard(classes, "gradient")}
                                </div>

                                <Divider style={{ marginTop: 16 }} />

                                {/* 2.3 Textures — one strip per category */}
                                <div className={classes.subSectionHeader}>
                                    <span className={classes.subSectionLabel}>Textures</span>
                                    <Button size="small" variant="outlined" color="primary" startIcon={<AddIcon />}
                                        style={{ textTransform: "capitalize", fontSize: 12 }}
                                        onClick={() => this.openPopup("textureCategory")}>
                                        Add Category
                                    </Button>
                                </div>

                                {texCats.length === 0 && (
                                    <EmptyStrip classes={classes} label="No texture categories yet." />
                                )}
                                {texCats.map(tc => (
                                    <div key={tc.id} style={{ marginBottom: 16 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                            <Typography variant="body2" style={{ fontWeight: 600, fontSize: 12, color: "#666" }}>
                                                {tc.categoryLabel}
                                            </Typography>
                                            <IconButton size="small" color="secondary"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorTextureCategoryDelete`, tc.id)}>
                                                <DeleteIcon style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </div>
                                        <div className={classes.hScroll}>
                                            {(tc.items || []).length === 0 && <EmptyStrip classes={classes} label="No textures yet." />}
                                            {(tc.items || []).map(item => (
                                                <div key={item.id} className={`${classes.itemCard} ${classes.itemCardWide}`}>
                                                    <img src={getImageUrl(item.fileName)} alt={item.label} className={classes.thumb}
                                                        onError={e => { e.target.style.display = "none"; }} />
                                                    <span className={classes.itemLabel}>{item.label || "—"}</span>
                                                    <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                        onClick={() => this.del(`${this.BASE()}/C21ProductEditorTextureItemDelete`, item.id)}>
                                                        <DeleteIcon style={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </div>
                                            ))}
                                            {this.addCard(classes, "textureItem", () => {
                                                this.setState({ popTexItemCatId: String(tc.id) });
                                                this.openPopup("textureItem");
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </Paper>}

                            {/* ════════════════════════════════════════════ */}
                            {/* SECTION 3 — STICKERS                        */}
                            {/* ════════════════════════════════════════════ */}
                            <Paper className={classes.sectionCard} elevation={0}>
                                <div className={classes.sectionTitle}>
                                    <span>😊</span> Stickers
                                </div>

                                {/* Category dropdown + Add Category */}
                                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 14 }}>
                                    <Grid item xs={12} sm={5}>
                                        <FormControl variant="outlined" fullWidth size="small">
                                            <InputLabel>Sticker Category</InputLabel>
                                            <Select name="selectedStickerCategoryId" value={selectedStickerCategoryId}
                                                onChange={this.handleChange} label="Sticker Category">
                                                {stickerCats.length === 0
                                                    ? <MenuItem value=""><em>No categories yet</em></MenuItem>
                                                    : stickerCats.map(sc => (
                                                        <MenuItem key={sc.id} value={String(sc.id)}>
                                                            {sc.categoryLabel}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item>
                                        <Button size="small" variant="outlined" color="primary" startIcon={<AddIcon />}
                                            style={{ textTransform: "capitalize", height: 40 }}
                                            onClick={() => this.openPopup("stickerCategory")}>
                                            Add Category
                                        </Button>
                                    </Grid>
                                    {activeStickerCat && (
                                        <Grid item>
                                            <IconButton size="small" color="secondary" title="Delete this category"
                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorStickerCategoryDelete`, activeStickerCat.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Grid>
                                    )}
                                </Grid>

                                {/* Sticker items strip */}
                                <div className={classes.hScroll}>
                                    {!activeStickerCat
                                        ? <EmptyStrip classes={classes} label="Select or create a sticker category first." />
                                        : stickerItems.length === 0
                                            ? <EmptyStrip classes={classes} label="No stickers in this category yet." />
                                            : stickerItems.map(s => (
                                                <div key={s.id} className={classes.itemCard}>
                                                    <img src={getImageUrl(s.fileName)} alt="sticker" className={classes.thumb}
                                                        onError={e => { e.target.style.display = "none"; }} />
                                                    <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                        onClick={() => this.del(`${this.BASE()}/C21ProductEditorStickerItemDelete`, s.id)}>
                                                        <DeleteIcon style={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </div>
                                            ))
                                    }
                                    {activeStickerCat && this.addCard(classes, "stickerItem", () => {
                                        this.setState({ popSticItemCatId: String(activeStickerCat.id) });
                                        this.openPopup("stickerItem");
                                    })}
                                </div>
                            </Paper>

                            {/* ════════════════════════════════════════════ */}
                            {/* SECTION 4 — COVER DESIGNS (category 1 only) */}
                            {/* ════════════════════════════════════════════ */}
                            {isCat1 && (
                                <Paper className={classes.sectionCard} elevation={0}>
                                    <div className={classes.sectionTitle}>
                                        <span>🎨</span> Cover Designs
                                    </div>

                                    {/* 4.1 Solid Colors */}
                                    {this.subHeader(classes, "Solid Colors", "coverSolidColor")}
                                    <div className={classes.hScroll}>
                                        {coverSolidColors.length === 0 && <EmptyStrip classes={classes} />}
                                        {coverSolidColors.map((hex, i) => (
                                            <div key={i} className={classes.itemCard}>
                                                <div className={classes.colorSwatch} style={{ backgroundColor: hex }} />
                                                <span className={classes.itemLabel}>{hex}</span>
                                                <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                    onClick={() => this.del(`${this.BASE()}/C21ProductEditorCoverDesignSolidColorDelete`, i)}>
                                                    <DeleteIcon style={{ fontSize: 13 }} />
                                                </IconButton>
                                            </div>
                                        ))}
                                        {this.addCard(classes, "coverSolidColor")}
                                    </div>

                                    <Divider style={{ marginTop: 16 }} />

                                    {/* 4.2 Patterns */}
                                    {this.subHeader(classes, "Patterns", "pattern")}
                                    {groupedPatterns.length === 0 && <EmptyStrip classes={classes} label="No patterns yet." />}
                                    {groupedPatterns.map(group => (
                                        <div key={group.sizeId} style={{ marginBottom: 12 }}>
                                            {/* Size group header */}
                                            <Typography variant="caption" style={{
                                                display: "inline-block", marginBottom: 6, marginLeft: 2,
                                                background: "#e3eaf3", color: "#1565c0", borderRadius: 4,
                                                padding: "2px 8px", fontWeight: 600, fontSize: 11,
                                            }}>
                                                {patternSizes.find(s => String(s.id) === String(group.sizeId))?.label || `Size ${group.sizeId}`}
                                            </Typography>
                                            <div className={classes.hScroll}>
                                                {(group.items || []).map(p => {
                                                    const tileSize = !p.isBackCoverSame ? 44 : 54;
                                                    return (
                                                        <div key={p.id} className={classes.itemCard} style={{ minWidth: !p.isBackCoverSame ? 190 : 140, maxWidth: !p.isBackCoverSame ? 210 : 160 }}>
                                                            <div style={{ display: "flex", gap: 4, width: "100%" }}>
                                                                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                                                    <img src={getImageUrl(p.icFileName)} alt="IC"
                                                                        style={{ width: tileSize, height: tileSize, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }}
                                                                        onError={e => { e.target.style.display = "none"; }} />
                                                                    <Typography variant="caption" style={{ fontSize: 10, color: "#888" }}>IC</Typography>
                                                                </div>
                                                                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                                                    <img src={getImageUrl(p.flFileName)} alt="FL"
                                                                        style={{ width: tileSize, height: tileSize, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }}
                                                                        onError={e => { e.target.style.display = "none"; }} />
                                                                    <Typography variant="caption" style={{ fontSize: 10, color: "#888" }}>FL</Typography>
                                                                </div>
                                                                {!p.isBackCoverSame && (
                                                                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                                                        <div style={{ width: tileSize, height: tileSize, borderRadius: 6, border: "1px solid #ddd", backgroundColor: p.backCoverColor || "#eee" }} />
                                                                        <Typography variant="caption" style={{ fontSize: 10, color: "#888" }}>BG</Typography>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 6, wordBreak: "break-word", whiteSpace: "normal", width: "100%" }}>{p.label}</span>
                                                            <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                                onClick={() => this.del(`${this.BASE()}/C21ProductEditorPatternDelete`, p.id)}>
                                                                <DeleteIcon style={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    {this.addCard(classes, "pattern")}

                                    <Divider style={{ marginTop: 16 }} />

                                    {/* 4.3 Two-Tone Colors */}
                                    {this.subHeader(classes, "Two-Tone Colors", "twoToneColor")}
                                    <div className={classes.hScroll}>
                                        {twoTuneColors.length === 0 && <EmptyStrip classes={classes} label="No two-tone colors yet." />}
                                        {twoTuneColors.map(item => {
                                            const totalH = 46;
                                            const masterH = Math.round((Number(item.masterColorPercentage) / 100) * totalH);
                                            const childH = Math.round((Number(item.childColorPercentage) / 100) * totalH);
                                            const sepH = Math.max(totalH - masterH - childH, 1);
                                            return (
                                                <div key={item.id} className={classes.itemCard}>
                                                    <div className={classes.colorSwatch} style={{ overflow: "hidden" }}>
                                                        <div style={{ height: masterH, backgroundColor: item.masterColor }} />
                                                        <div style={{ height: sepH, backgroundColor: item.separatorColor }} />
                                                        <div style={{ height: childH, backgroundColor: item.childColor }} />
                                                    </div>
                                                    <span className={classes.itemLabel}>{item.name}</span>
                                                    <IconButton className={classes.itemDeleteBtn} size="small" color="secondary"
                                                        onClick={() => this.del(`${this.BASE()}/C21ProductEditorTwoToneColorDelete`, item.id)}>
                                                        <DeleteIcon style={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </div>
                                            );
                                        })}
                                        {this.addCard(classes, "twoToneColor")}
                                    </div>
                                </Paper>
                            )}

                        </Fragment>
                    )}
                </div>

                {/* ══════════════════════════════════════════════════════════ */}
                {/* POPUPS                                                    */}
                {/* ══════════════════════════════════════════════════════════ */}

                {/* ── Text Color ───────────────────────────────────────────── */}
                <Dialog open={popup === "textColor"} onClose={this.closePopup} maxWidth="xs" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Text Color</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <TextField autoComplete="off" name="popTextColor" label="Hex Color (e.g. #FF5733)"
                            variant="outlined" fullWidth value={popTextColor} onChange={this.handleChange}
                            error={!!popTextColorError} helperText={popTextColorError}
                            inputProps={{ maxLength: 7 }} style={{ marginTop: 8 }} />
                        {HEX_REGEX.test(popTextColor) && (
                            <div style={{
                                width: "100%", height: 40, borderRadius: 8, border: "1px solid #ddd",
                                backgroundColor: popTextColor, marginTop: 12
                            }} />
                        )}
                        <FormControlLabel
                            style={{ marginTop: 12 }}
                            control={
                                <Checkbox
                                    checked={popTextColorIsForCover}
                                    onChange={e => this.setState({ popTextColorIsForCover: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label="For Cover"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveTextColor}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Font ─────────────────────────────────────────────────── */}
                <Dialog open={popup === "font"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Font</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popFontLabel" label="Font Label (e.g. Poppins)"
                                    variant="outlined" fullWidth value={popFontLabel} onChange={this.handleChange}
                                    error={!!popFontLabelError} helperText={popFontLabelError} />
                            </Grid>
                            {/* ── Font file uploader ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: "block" }}>
                                    Font File (.ttf, .otf)
                                </Typography>
                                {this.renderFileUploader(classes, "popFontFile", "popFontFilePreview", ".ttf,.otf,.woff,.woff2", "Click to upload font file")}
                                {popFontFileNameError && (
                                    <Typography variant="caption" style={{ color: "#f44336", marginTop: 4, display: "block" }}>
                                        {popFontFileNameError}
                                    </Typography>
                                )}
                            </Grid>
                            {/* ── OR type path manually ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                                    — or enter path manually (if file is already on server) —
                                </Typography>
                                <TextField autoComplete="off" name="popFontFileName"
                                    label="File Path (e.g. product-editor-config/fonts/poppins.ttf)"
                                    variant="outlined" fullWidth size="small" value={popFontFile ? popFontFile.name : popFontFileName}
                                    onChange={this.handleChange}
                                    disabled={!!popFontFile}
                                    helperText={popFontFile ? "Using uploaded file — clear the upload to type manually" : ""} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField autoComplete="off" name="popFontBaseSizeUnits"
                                    label="Base Size Units (e.g. 88)" variant="outlined" fullWidth type="number"
                                    value={popFontBaseSizeUnits} onChange={this.handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={popFontIsForCover}
                                            onChange={e => this.setState({ popFontIsForCover: e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label="For Cover"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveFont}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Solid Color ───────────────────────────────────────────── */}
                <Dialog open={popup === "solidColor"} onClose={this.closePopup} maxWidth="xs" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Border Solid Color</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <TextField autoComplete="off" name="popSolidColor" label="Hex Color (e.g. #FF0000)"
                            variant="outlined" fullWidth value={popSolidColor} onChange={this.handleChange}
                            error={!!popSolidColorError} helperText={popSolidColorError}
                            inputProps={{ maxLength: 7 }} style={{ marginTop: 8 }} />
                        {HEX_REGEX.test(popSolidColor) && (
                            <div style={{
                                width: "100%", height: 40, borderRadius: 8, border: "1px solid #ddd",
                                backgroundColor: popSolidColor, marginTop: 12
                            }} />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveSolidColor}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Gradient ──────────────────────────────────────────────── */}
                <Dialog open={popup === "gradient"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Gradient</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            <Grid item xs={8}>
                                <TextField autoComplete="off" name="popGradName" label="Name (e.g. Sunset)"
                                    variant="outlined" fullWidth value={popGradName} onChange={this.handleChange}
                                    error={!!popGradNameError} helperText={popGradNameError} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField autoComplete="off" name="popGradAngle" label="Angle °"
                                    variant="outlined" fullWidth type="number" value={popGradAngle}
                                    onChange={this.handleChange} error={!!popGradAngleError} helperText={popGradAngleError} />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                    Colors — add at least 2, press Enter or click Add after each
                                </Typography>
                                <Grid container spacing={1} alignItems="center" style={{ marginTop: 6 }}>
                                    <Grid item xs={7}>
                                        <TextField autoComplete="off" name="popGradColorInput" label="Hex Color"
                                            variant="outlined" size="small" fullWidth value={popGradColorInput}
                                            onChange={this.handleChange}
                                            onKeyDown={e => { if (e.key === "Enter") this.addGradChip(); }}
                                            inputProps={{ maxLength: 7 }} />
                                    </Grid>
                                    {HEX_REGEX.test(popGradColorInput) && (
                                        <Grid item>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: 6,
                                                backgroundColor: popGradColorInput, border: "1px solid #ccc"
                                            }} />
                                        </Grid>
                                    )}
                                    <Grid item>
                                        <Button size="small" variant="outlined" color="primary"
                                            onClick={this.addGradChip} startIcon={<AddIcon />}
                                            style={{ textTransform: "capitalize" }}>
                                            Add
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {popGradColors.length > 0 && (
                                <Grid item xs={12}>
                                    {popGradColors.length >= 2 && (
                                        <div style={{
                                            height: 36, borderRadius: 8, border: "1px solid #ddd", marginBottom: 8,
                                            background: `linear-gradient(${popGradAngle || 135}deg, ${popGradColors.join(", ")})`
                                        }} />
                                    )}
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {popGradColors.map((col, i) => (
                                            <Chip key={i} label={col} size="small"
                                                style={{ backgroundColor: col, color: "#fff", fontWeight: 600 }}
                                                onDelete={() => this.removeGradChip(i)} />
                                        ))}
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveGradient}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Texture Category ──────────────────────────────────────── */}
                <Dialog open={popup === "textureCategory"} onClose={this.closePopup} maxWidth="xs" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Texture Category</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <TextField autoComplete="off" name="popTexCatLabel" label="Category Label (e.g. All)"
                            variant="outlined" fullWidth value={popTexCatLabel} onChange={this.handleChange}
                            error={!!popTexCatLabelError} helperText={popTexCatLabelError} style={{ marginTop: 8 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveTextureCategory}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Texture Item ──────────────────────────────────────────── */}
                <Dialog open={popup === "textureItem"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Texture Item</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth error={!!popTexItemCatIdError}>
                                    <InputLabel>Texture Category</InputLabel>
                                    <Select name="popTexItemCatId" value={popTexItemCatId}
                                        onChange={this.handleChange} label="Texture Category">
                                        {texCats.map(tc => (
                                            <MenuItem key={tc.id} value={String(tc.id)}>{tc.categoryLabel}</MenuItem>
                                        ))}
                                    </Select>
                                    {popTexItemCatIdError && <FormHelperText>{popTexItemCatIdError}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popTexItemLabel" label="Label (optional)"
                                    variant="outlined" fullWidth value={popTexItemLabel} onChange={this.handleChange} />
                            </Grid>
                            {/* ── Texture image uploader ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: "block" }}>
                                    Texture Image
                                </Typography>
                                {this.renderFileUploader(classes, "popTexItemFile", "popTexItemFilePreview", "image/*", "Click to upload texture image")}
                                {popTexItemFileNameError && (
                                    <Typography variant="caption" style={{ color: "#f44336", marginTop: 4, display: "block" }}>
                                        {popTexItemFileNameError}
                                    </Typography>
                                )}
                            </Grid>
                            {/* ── OR type path manually ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                                    — or enter path manually —
                                </Typography>
                                <TextField autoComplete="off" name="popTexItemFileName"
                                    label="File Path (e.g. product-editor-config/textures/wood.png)"
                                    variant="outlined" fullWidth size="small"
                                    value={popTexItemFile ? popTexItemFile.name : popTexItemFileName}
                                    onChange={this.handleChange}
                                    disabled={!!popTexItemFile}
                                    helperText={popTexItemFile ? "Using uploaded file" : ""} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveTextureItem}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Sticker Category ──────────────────────────────────────── */}
                <Dialog open={popup === "stickerCategory"} onClose={this.closePopup} maxWidth="xs" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Sticker Category</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <TextField autoComplete="off" name="popSticCatLabel" label="Category Label (e.g. Emoji)"
                            variant="outlined" fullWidth value={popSticCatLabel} onChange={this.handleChange}
                            error={!!popSticCatLabelError} helperText={popSticCatLabelError} style={{ marginTop: 8 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveStickerCategory}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Sticker Item ──────────────────────────────────────────── */}
                <Dialog open={popup === "stickerItem"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Sticker</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth error={!!popSticItemCatIdError}>
                                    <InputLabel>Sticker Category</InputLabel>
                                    <Select name="popSticItemCatId" value={popSticItemCatId}
                                        onChange={this.handleChange} label="Sticker Category">
                                        {stickerCats.map(sc => (
                                            <MenuItem key={sc.id} value={String(sc.id)}>{sc.categoryLabel}</MenuItem>
                                        ))}
                                    </Select>
                                    {popSticItemCatIdError && <FormHelperText>{popSticItemCatIdError}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            {/* ── Sticker image uploader ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: "block" }}>
                                    Sticker Image (.png recommended — transparent background)
                                </Typography>
                                {this.renderFileUploader(classes, "popSticItemFile", "popSticItemFilePreview", "image/*", "Click to upload sticker image")}
                                {popSticItemFileNameError && (
                                    <Typography variant="caption" style={{ color: "#f44336", marginTop: 4, display: "block" }}>
                                        {popSticItemFileNameError}
                                    </Typography>
                                )}
                            </Grid>
                            {/* ── OR type path manually ── */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                                    — or enter path manually —
                                </Typography>
                                <TextField autoComplete="off" name="popSticItemFileName"
                                    label="File Path (e.g. product-editor-config/stickers/smile.png)"
                                    variant="outlined" fullWidth size="small"
                                    value={popSticItemFile ? popSticItemFile.name : popSticItemFileName}
                                    onChange={this.handleChange}
                                    disabled={!!popSticItemFile}
                                    helperText={popSticItemFile ? "Using uploaded file" : ""} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveStickerItem}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Cover Design Solid Color ──────────────────────────────── */}
                <Dialog open={popup === "coverSolidColor"} onClose={this.closePopup} maxWidth="xs" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Cover Solid Color</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <TextField autoComplete="off" name="popCoverSolidColor" label="Hex Color (e.g. #FF0000)"
                            variant="outlined" fullWidth value={popCoverSolidColor} onChange={this.handleChange}
                            error={!!popCoverSolidColorError} helperText={popCoverSolidColorError}
                            inputProps={{ maxLength: 7 }} style={{ marginTop: 8 }} />
                        {HEX_REGEX.test(popCoverSolidColor) && (
                            <div style={{
                                width: "100%", height: 40, borderRadius: 8, border: "1px solid #ddd",
                                backgroundColor: popCoverSolidColor, marginTop: 12
                            }} />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveCoverSolidColor}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Pattern ───────────────────────────────────────────────── */}
                <Dialog open={popup === "pattern"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Pattern</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        {isPatternDataLoading ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                                <CircularProgress size={28} />
                            </div>
                        ) : (
                            <Grid container spacing={2} style={{ marginTop: 4 }}>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth error={!!popPatternSizeIdError}>
                                        <InputLabel>Size</InputLabel>
                                        <Select name="popPatternSizeId" value={popPatternSizeId}
                                            onChange={this.handleChange} label="Size">
                                            {patternSizes.map(s => (
                                                <MenuItem key={s.id} value={String(s.id)}>{s.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {popPatternSizeIdError && <FormHelperText>{popPatternSizeIdError}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth error={!!popPatternCoverTypeIdError}>
                                        <InputLabel>Cover Type</InputLabel>
                                        <Select name="popPatternCoverTypeId" value={popPatternCoverTypeId}
                                            onChange={this.handleChange} label="Cover Type">
                                            {patternCoverTypes.map(ct => (
                                                <MenuItem key={ct.id} value={String(ct.id)}>{ct.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {popPatternCoverTypeIdError && <FormHelperText>{popPatternCoverTypeIdError}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth error={!!popPatternPageNumbersError}>
                                        <InputLabel>Page Numbers</InputLabel>
                                        <Select multiple name="popPatternPageNumbers" value={popPatternPageNumbers}
                                            onChange={(e) => this.setState({ popPatternPageNumbers: e.target.value, popPatternPageNumbersError: "" })}
                                            label="Page Numbers"
                                            renderValue={(selected) => selected.map(id => patternPageNumbers.find(p => String(p.id) === String(id))?.label || id).join(", ")}>
                                            {patternPageNumbers.map(p => (
                                                <MenuItem key={p.id} value={String(p.id)}>{p.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {popPatternPageNumbersError && <FormHelperText>{popPatternPageNumbersError}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth error={!!popPatternPageTypeIdError}>
                                        <InputLabel>Page Type</InputLabel>
                                        <Select name="popPatternPageTypeId" value={popPatternPageTypeId}
                                            onChange={this.handleChange} label="Page Type">
                                            {patternPageTypes.map(pt => (
                                                <MenuItem key={pt.id} value={String(pt.id)}>{pt.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {popPatternPageTypeIdError && <FormHelperText>{popPatternPageTypeIdError}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                                {popPatternSizeId && popPatternCoverTypeId && popPatternPageNumbers.length > 0 && popPatternPageTypeId && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="textSecondary">
                                            Label preview: <strong>
                                                {`${patternSizes.find(s => String(s.id) === String(popPatternSizeId))?.label || popPatternSizeId}_${patternCoverTypes.find(c => String(c.id) === String(popPatternCoverTypeId))?.label || popPatternCoverTypeId}_${patternPageTypes.find(pt => String(pt.id) === String(popPatternPageTypeId))?.label || popPatternPageTypeId}_${popPatternPageNumbers.join("_")}`}
                                            </strong>
                                        </Typography>
                                    </Grid>
                                )}
                                {/* IC Asset */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: "block" }}>
                                        IC Asset
                                    </Typography>
                                    {this.renderFileUploader(classes, "popPatternIcFile", "popPatternIcFilePreview", "image/*", "Click to upload IC Asset")}
                                    {popPatternIcFileError && (
                                        <Typography variant="caption" style={{ color: "#f44336", marginTop: 4, display: "block" }}>
                                            {popPatternIcFileError}
                                        </Typography>
                                    )}
                                </Grid>
                                {/* FL Asset */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: "block" }}>
                                        FL Asset
                                    </Typography>
                                    {this.renderFileUploader(classes, "popPatternFlFile", "popPatternFlFilePreview", "image/*", "Click to upload FL Asset")}
                                    {popPatternFlFileError && (
                                        <Typography variant="caption" style={{ color: "#f44336", marginTop: 4, display: "block" }}>
                                            {popPatternFlFileError}
                                        </Typography>
                                    )}
                                </Grid>
                                {/* isBackCoverSame toggle */}
                                <Grid item xs={12}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <input
                                            type="checkbox"
                                            id="isBackCoverSame"
                                            checked={popPatternIsBackCoverSame}
                                            onChange={(e) => this.setState({ popPatternIsBackCoverSame: e.target.checked, popPatternBackCoverColorError: "" })}
                                            style={{ width: 16, height: 16, cursor: "pointer" }}
                                        />
                                        <label htmlFor="isBackCoverSame" style={{ fontSize: 13, color: "#333", cursor: "pointer" }}>
                                            Back cover same
                                        </label>
                                    </div>
                                </Grid>
                                {/* Back cover color — only shown when isBackCoverSame is false */}
                                {!popPatternIsBackCoverSame && (
                                    <Grid item xs={12}>
                                        <TextField autoComplete="off" name="popPatternBackCoverColor" label="Back Cover Color (hex)"
                                            variant="outlined" fullWidth value={popPatternBackCoverColor}
                                            onChange={this.handleChange}
                                            error={!!popPatternBackCoverColorError} helperText={popPatternBackCoverColorError}
                                            inputProps={{ maxLength: 7 }} />
                                        {HEX_REGEX.test(popPatternBackCoverColor) && (
                                            <div style={{ width: "100%", height: 32, borderRadius: 6, border: "1px solid #ddd", backgroundColor: popPatternBackCoverColor, marginTop: 8 }} />
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.savePattern}
                            disabled={isSaving || isPatternDataLoading} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Two-Tone Color ────────────────────────────────────────── */}
                <Dialog open={popup === "twoToneColor"} onClose={this.closePopup} maxWidth="sm" fullWidth>
                    <DialogTitle disableTypography>
                        <div className={classes.dialogTitleRow}>
                            <Typography variant="h6">Add Two-Tone Color</Typography>
                            <IconButton size="small" onClick={this.closePopup}><CloseIcon /></IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popTwoToneName" label="Name"
                                    variant="outlined" fullWidth value={popTwoToneName} onChange={this.handleChange}
                                    error={!!popTwoToneNameError} helperText={popTwoToneNameError} />
                            </Grid>
                            {/* Master Color */}
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popTwoToneMasterColor" label="Master Color (hex)"
                                    variant="outlined" fullWidth value={popTwoToneMasterColor} onChange={this.handleChange}
                                    error={!!popTwoToneMasterColorError} helperText={popTwoToneMasterColorError}
                                    inputProps={{ maxLength: 7 }} />
                                {HEX_REGEX.test(popTwoToneMasterColor) && (
                                    <div style={{ width: "100%", height: 32, borderRadius: 6, border: "1px solid #ddd", backgroundColor: popTwoToneMasterColor, marginTop: 6 }} />
                                )}
                            </Grid>
                            {/* Separator Color */}
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popTwoToneSeparatorColor" label="Separator Color (hex)"
                                    variant="outlined" fullWidth value={popTwoToneSeparatorColor} onChange={this.handleChange}
                                    error={!!popTwoToneSeparatorColorError} helperText={popTwoToneSeparatorColorError}
                                    inputProps={{ maxLength: 7 }} />
                                {HEX_REGEX.test(popTwoToneSeparatorColor) && (
                                    <div style={{ width: "100%", height: 32, borderRadius: 6, border: "1px solid #ddd", backgroundColor: popTwoToneSeparatorColor, marginTop: 6 }} />
                                )}
                            </Grid>
                            {/* Child Color */}
                            <Grid item xs={12}>
                                <TextField autoComplete="off" name="popTwoToneChildColor" label="Child Color (hex)"
                                    variant="outlined" fullWidth value={popTwoToneChildColor} onChange={this.handleChange}
                                    error={!!popTwoToneChildColorError} helperText={popTwoToneChildColorError}
                                    inputProps={{ maxLength: 7 }} />
                                {HEX_REGEX.test(popTwoToneChildColor) && (
                                    <div style={{ width: "100%", height: 32, borderRadius: 6, border: "1px solid #ddd", backgroundColor: popTwoToneChildColor, marginTop: 6 }} />
                                )}
                            </Grid>
                            {/* Combined preview — fixed percentages: Master 79%, Separator 2%, Child 19% */}
                            {HEX_REGEX.test(popTwoToneMasterColor) && HEX_REGEX.test(popTwoToneSeparatorColor) && HEX_REGEX.test(popTwoToneChildColor) && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6 }}>
                                        Combined preview (79% · 2% · 19%)
                                    </Typography>
                                    <div style={{ width: "100%", height: 80, borderRadius: 8, border: "1px solid #ddd", overflow: "hidden" }}>
                                        <div style={{ height: "79%", backgroundColor: popTwoToneMasterColor }} />
                                        <div style={{ height: "2%", backgroundColor: popTwoToneSeparatorColor }} />
                                        <div style={{ height: "19%", backgroundColor: popTwoToneChildColor }} />
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closePopup} style={{ textTransform: "capitalize" }}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.saveTwoToneColor}
                            disabled={isSaving} style={{ textTransform: "capitalize" }}>
                            {isSaving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Fragment>
        );
    }
}

F21ProductEditorConfigForm.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(useStyles)(F21ProductEditorConfigForm);